import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { MaskPipe } from 'ngx-mask';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Utils, { UtilsValidators } from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-company-users',
  templateUrl: './dashboard-company-users.component.html',
  styleUrls: ['./dashboard-company-users.component.scss'],
})
export class DashboardCompanyUsersComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() activeTab: string = '';

  private destroy$ = new Subject<void>();

  permissionObj = {
    canAdd: true,
    canFilter: true,
    canChange: true,
  };

  data: any[] = [];
  loading = true;
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  resultsPerPage = 10;
  totalPages: number = null;
  currentPage: number = 1;

  userTypeOptions = [
    { label: 'Master', value: 'master' },
    { label: 'Comum', value: 'common' },
  ];

  filterForm = this.formBuilder.group({
    name: [null, []],
    document_number: [null, [Utils.validateCpfCnpj]],
    email: [null, [UtilsValidators.email]],
    user_type: [null, []],
  });

  // Modal de trocar departamento
  showChangeDepartmentModal = false;
  selectedUserForDepartmentChange: any = null;
  departmentOptions: Array<{ label: string; value: number }> = [];
  savingDepartment = false;
  loadingDepartments = false;

  changeDepartmentForm = this.formBuilder.group({
    department: [null, [Validators.required]],
  });

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private router: Router,
    private toast: ToastService,
    private companyService: CompanyService,
    private maskPipe: MaskPipe
  ) {}

  ngOnInit(): void {
    if (this.activeTab === 'users') {
      this.getData();
    }

    // Listener para quando um usuário for adicionado
    this.companyService.userAdded
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loading = true;
        this.getData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'] && changes['activeTab'].currentValue === 'users') {
      if (!changes['activeTab'].isFirstChange()) {
        this.getData();
      }
    }
  }

  async getData(page: number = 1) {
    this.loading = true;

    try {
      const filters: any = {};

      // Construir filtros
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            let apiKey = key;

            // Mapear campos do formulário para os parâmetros da API
            if (key === 'document_number') {
              newValue = value.replace(/[^\d]/g, '');
              apiKey = 'cpf'; // API espera 'cpf', não 'document_number'
            }

            filters[apiKey] = newValue;
          }
        }
      });

      const response: any = await this.api.get({
        route: `api/collaborator/`,
        token: true,
        params: {
          order_by: '-created_at',
          page: page,
          ...filters,
        },
      });

      // Usar results_per_page da API quando disponível
      this.resultsPerPage =
        response.results_per_page || response.resultsPerPage || 10;
      this.offset = response.offset || 0;
      this.count = response.count || 0;
      this.totalPages = response.total_pages || null;
      this.currentPage = response.current_page || page;
      this.data = response.data || [];
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os colaboradores.'
      );
    } finally {
      this.loading = false;
    }
  }

  refreshData() {
    this.getData(1);
  }

  changePageData(page: number) {
    this.getData(page);
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;
  }

  async filterData() {
    this.hasFilters = true;
    this.showFilterModal = false;
    await this.getData(1);
    this.updateAppliedFilters();
  }

  resetForm() {
    this.filterForm.reset();
    this.hasFilters = false;
    this.appliedFilters = [];
    this.showFilterModal = false;
    this.getData(1);
  }

  updateAppliedFilters() {
    this.appliedFilters = [];
    const values = this.filterForm.value;

    const fieldLabels: any = {
      name: 'Nome',
      document_number: 'CPF',
      email: 'Email',
      user_type: 'Tipo de usuário',
    };

    const userTypeLabels: any = {
      master: 'Master',
      common: 'Comum',
    };

    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '') {
        if (fieldLabels[key]) {
          let displayValue = String(value);

          // Formatar valor do CPF (remover máscara)
          if (key === 'document_number') {
            displayValue = value.replace(/[^\d]/g, '');
          }

          // Formatar valor do tipo de usuário
          if (key === 'user_type') {
            displayValue = userTypeLabels[value] || value;
          }

          // Formatar como "Label: valor"
          this.appliedFilters.push({
            label: `${fieldLabels[key]}: ${displayValue}`,
            value: '',
          });
        }
      }
    });
  }

  formatCpf(cpf: string): string {
    if (!cpf) return '-';
    return this.maskPipe.transform(cpf, '000.000.000-00');
  }

  getDepartmentName(user: any): string {
    // Se for Master, mostrar "Acesso a todos"
    if (user.user_type === 'master' || user.is_master) {
      return 'Acesso a todos';
    }

    // Caso contrário, mostrar o nome do departamento
    if (user.department_data && user.department_data.name) {
      return user.department_data.name;
    }
    return '-';
  }

  getUserType(user: any): string {
    if (user.user_type === 'master' || user.is_master) {
      return 'Master';
    } else if (user.user_type === 'owner') {
      return 'Owner';
    } else if (user.user_type === 'common') {
      return 'Comum';
    }
    return user.user_type || '-';
  }

  displayDateTime(dateString: string) {
    if (!dateString) return { date: '-', time: '-' };

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      date: formattedDate,
      time: formattedTime,
    };
  }

  getStatusDisplay(user: any): string {
    // Colaboradores geralmente estão sempre ativos se estão na lista
    // Pode adicionar lógica específica se houver campo de status
    return 'Ativo';
  }

  viewDetails(user: any) {
    // Navegar para detalhes do colaborador se necessário
  }

  isCommonUser(user: any): boolean {
    return user.user_type === 'common' && !user.is_master;
  }

  async openChangeDepartmentModal(user: any) {
    // Validar se é usuário comum
    if (!this.isCommonUser(user)) {
      this.toast.show(
        'error',
        'Erro',
        'Apenas usuários comuns podem ter o departamento alterado.'
      );
      return;
    }

    this.loadingDepartments = true;
    this.selectedUserForDepartmentChange = user;

    try {
      // Buscar departamentos se ainda não foram carregados
      if (this.departmentOptions.length === 0) {
        await this.getDepartments();
      }

      // Preencher o formulário com o departamento atual do usuário
      const currentDepartmentId = user.department_data?.id || user.department;
      this.changeDepartmentForm.patchValue({
        department: currentDepartmentId,
      });

      this.showChangeDepartmentModal = true;
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingDepartments = false;
    }
  }

  closeChangeDepartmentModal() {
    this.showChangeDepartmentModal = false;
    this.selectedUserForDepartmentChange = null;
    this.changeDepartmentForm.reset();
  }

  async getDepartments() {
    try {
      const response: any = await this.api.get({
        route: 'department/',
        token: true,
      });

      this.departmentOptions = (response || []).map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao carregar os departamentos.'
      );
    }
  }

  async saveDepartmentChange() {
    if (
      !this.selectedUserForDepartmentChange ||
      !this.changeDepartmentForm.valid
    ) {
      return;
    }

    const departmentId = this.changeDepartmentForm.get('department')?.value;

    if (!departmentId) {
      this.toast.show('error', 'Erro', 'Selecione um departamento.');
      return;
    }

    this.savingDepartment = true;

    try {
      await this.api.patch({
        route: `api/collaborator/${this.selectedUserForDepartmentChange.id}/`,
        token: true,
        body: {
          department: departmentId,
        },
      });

      this.toast.show('info', 'Sucesso', 'Departamento alterado com sucesso.');
      this.closeChangeDepartmentModal();

      // Remover loading antes de atualizar a lista (deixa só o skeleton)
      this.savingDepartment = false;

      // Atualizar a lista de usuários (mostra skeleton durante o get)
      await this.getData(this.currentPage);
    } catch (error: any) {
      console.error(error);

      // Verificar se é erro de tentar alterar master
      const errorMessage = error?.error?.message || error?.message || '';
      if (errorMessage.includes('master') || errorMessage.includes('Master')) {
        this.toast.show(
          'error',
          'Erro',
          'Não é possível alterar o departamento de um colaborador master.'
        );
      } else {
        this.toast.show(
          'error',
          'Erro',
          'Ocorreu um erro ao alterar o departamento.'
        );
      }
      this.savingDepartment = false;
    }
  }

  get isLoadingDepartmentAction(): boolean {
    return this.loadingDepartments || this.savingDepartment;
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
